import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import './Notifications.css';

interface Notification {
  id: number;
  user_id: number;
  type: 'friend_request' | 'friend_accepted' | 'temp_location_shared' | 'friend_nearby' | 'friend_in_same_city';
  title: string;
  message: string;
  link?: string;
  related_user_id?: number;
  related_resource_id?: number;
  is_read: boolean;
  created_at: string;
  related_user_first_name?: string;
  related_user_last_name?: string;
  related_user_profile_photo?: string;
}

type FilterType = 'all' | 'unread' | 'read';

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [swipeState, setSwipeState] = useState<{
    id: number | null;
    offset: number;
    direction: 'left' | 'right' | null;
  }>({ id: null, offset: 0, direction: null });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsAPI.getAll(); // Get all notifications (no limit)
      setNotifications(data.notifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    try {
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleDeleteAllRead = async () => {
    if (!window.confirm('Supprimer toutes les notifications lues ?')) return;

    try {
      await notificationsAPI.deleteAllRead();
      setNotifications(prev => prev.filter(notif => !notif.is_read));
    } catch (error) {
      console.error('Failed to delete all read notifications:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleToggleReadStatus = async (notificationId: number, currentIsRead: boolean, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: !notif.is_read } : notif
        )
      );
    } catch (error) {
      console.error('Failed to toggle read status:', error);
    }
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, notificationId: number) => {
    setTouchStart(e.targetTouches[0].clientX);
    setSwipeState({ id: notificationId, offset: 0, direction: null });
  };

  const handleTouchMove = (e: React.TouchEvent, notificationId: number) => {
    if (!touchStart || swipeState.id !== notificationId) return;

    const currentTouch = e.targetTouches[0].clientX;
    const offset = currentTouch - touchStart;
    const direction = offset < 0 ? 'left' : 'right';

    setSwipeState({ id: notificationId, offset, direction });
  };

  const handleTouchEnd = async (notification: Notification) => {
    const { offset, direction } = swipeState;

    const DELETE_THRESHOLD = -150;
    const UNREAD_THRESHOLD = 150;

    if (direction === 'left' && offset < DELETE_THRESHOLD) {
      await handleDeleteNotification(notification.id);
    } else if (direction === 'right' && offset > UNREAD_THRESHOLD) {
      await handleToggleReadStatus(notification.id, notification.is_read);
    }

    setSwipeState({ id: null, offset: 0, direction: null });
    setTouchStart(null);
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'À l\'instant';
    if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `Il y a ${Math.floor(seconds / 86400)}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'friend_request':
        return '👋';
      case 'friend_accepted':
        return '✅';
      case 'temp_location_shared':
        return '📍';
      case 'friend_nearby':
        return '📌';
      case 'friend_in_same_city':
        return '🏙️';
      default:
        return '🔔';
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>Notifications</h1>
        <div className="notifications-actions">
          <button
            onClick={() => {
              navigate('/settings');
              // Scroll to notifications section after navigation
              setTimeout(() => {
                const notifSection = document.querySelector('.settings-section:has(h2[title="notifications"])');
                if (notifSection) {
                  notifSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            className="btn-settings"
            title="Gérer les réglages de notifications"
          >
            ⚙️ Réglages
          </button>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllAsRead} className="btn-secondary">
              Tout marquer comme lu ({unreadCount})
            </button>
          )}
          {notifications.some(n => n.is_read) && (
            <button onClick={handleDeleteAllRead} className="btn-danger">
              Supprimer les lues
            </button>
          )}
        </div>
      </div>

      <div className="notifications-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Toutes ({notifications.length})
        </button>
        <button
          className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Non lues ({unreadCount})
        </button>
        <button
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Lues ({notifications.length - unreadCount})
        </button>
      </div>

      <div className="notifications-list">
        {loading ? (
          <div className="notifications-loading">Chargement...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="notifications-empty">
            <span className="empty-icon">🔔</span>
            <p>
              {filter === 'unread' && 'Aucune notification non lue'}
              {filter === 'read' && 'Aucune notification lue'}
              {filter === 'all' && 'Aucune notification'}
            </p>
          </div>
        ) : (
          <>
            {filteredNotifications.map(notification => {
              const isThisNotificationSwiped = swipeState.id === notification.id;
              const offset = isThisNotificationSwiped ? swipeState.offset : 0;
              const direction = isThisNotificationSwiped ? swipeState.direction : null;

              const opacity = isThisNotificationSwiped && direction === 'left' && offset < -120
                ? Math.max(0.3, 1 + (offset + 120) / 60)
                : 1;

              let backgroundColor = '';
              if (isThisNotificationSwiped) {
                if (direction === 'left' && offset < -50) {
                  backgroundColor = `rgba(231, 76, 60, ${Math.min(0.3, Math.abs(offset) / 500)})`;
                } else if (direction === 'right' && offset > 50) {
                  backgroundColor = `rgba(74, 144, 226, ${Math.min(0.3, offset / 500)})`;
                }
              }

              return (
                <div key={notification.id} className="notification-item-wrapper">
                  {isThisNotificationSwiped && direction === 'left' && offset < -50 && (
                    <div className="swipe-hint swipe-hint-delete">Supprimer</div>
                  )}
                  {isThisNotificationSwiped && direction === 'right' && offset > 50 && (
                    <div className="swipe-hint swipe-hint-toggle">
                      {notification.is_read ? 'Marquer non lu' : 'Marquer lu'}
                    </div>
                  )}

                  <div
                    className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    onClick={() => !isThisNotificationSwiped && handleNotificationClick(notification)}
                    onTouchStart={(e) => handleTouchStart(e, notification.id)}
                    onTouchMove={(e) => handleTouchMove(e, notification.id)}
                    onTouchEnd={() => handleTouchEnd(notification)}
                    style={{
                      transform: `translateX(${offset}px)`,
                      opacity,
                      backgroundColor,
                      transition: isThisNotificationSwiped ? 'none' : 'all 0.3s ease',
                    }}
                  >
                    <div className="notification-icon">
                      {notification.related_user_profile_photo ? (
                        <img
                          src={getPhotoUrl(notification.related_user_profile_photo) || undefined}
                          alt=""
                          className="notification-user-photo"
                        />
                      ) : (
                        <span className="notification-type-icon">{getNotificationIcon(notification.type)}</span>
                      )}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{getTimeAgo(notification.created_at)}</div>
                    </div>

                    {!notification.is_read && (
                      <div
                        className="notification-unread-dot desktop-clickable"
                        onClick={(e) => handleToggleReadStatus(notification.id, notification.is_read, e)}
                        title="Marquer comme lu"
                      ></div>
                    )}

                    {notification.is_read && (
                      <div
                        className="notification-read-dot desktop-clickable"
                        onClick={(e) => handleToggleReadStatus(notification.id, notification.is_read, e)}
                        title="Marquer comme non lu"
                      ></div>
                    )}

                    <button
                      className="notification-delete-btn desktop-only"
                      onClick={(e) => handleDeleteNotification(notification.id, e)}
                      title="Supprimer"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
